import dayjs from "dayjs";
import {
  DayHoursList,
  HourObjectType,
  HourType,
  RestaurantDetails,
  RestaurantInfoDetails,
  RestaurantScheduleDetails,
  RestaurantType,
} from "src/business/restaurant/restaurant";
import {
  HourObjectReadResponse,
  RestaurantInfoReadResponse,
  RestaurantReadResponse,
  RestaurantResponse,
  RestaurantScheduleReadResponse,
  ScheduleEntryPayload,
  Weekday,
} from "src/data/types/restaurant";

const WEEK_DAYS = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
];

const closedDay = (day: string): HourType => ({
  day,
  morning: { start: dayjs("00:00", "HH:mm"), end: dayjs("00:00", "HH:mm") },
  evening: null,
});

export const mapHourObjectResponseToHourObject = (
  hourObj: HourObjectReadResponse,
): HourObjectType => ({
  start: dayjs(hourObj.start, "HH:mm"),
  end: dayjs(hourObj.end, "HH:mm"),
});

/** Convert an in-memory DayHoursList (from the schedule editor) to the payload the API expects. */
export const mapHoursToScheduleEntries = (
  hours: DayHoursList,
): ScheduleEntryPayload[] =>
  hours
    .filter(
      (h) =>
        h.morning.start.format("HH:mm") !== "00:00" ||
        h.morning.end.format("HH:mm") !== "00:00",
    )
    .map((h) => ({
      day: h.day.toUpperCase() as Weekday,
      morningStart: h.morning.start.format("HH:mm"),
      morningEnd: h.morning.end.format("HH:mm") === "00:00" ? "24:00" : h.morning.end.format("HH:mm"),
      eveningStart: h.evening?.start.format("HH:mm") ?? null,
      eveningEnd: h.evening?.end.format("HH:mm") === "00:00" ? "24:00" : (h.evening?.end.format("HH:mm") ?? null),
    }));

/**
 * Parse a legacy hours string (e.g. "Monday=09:00-14:00/18:00-22:00;Tuesday=...")
 * into the structured ScheduleEntryPayload array the API expects.
 */
export const parseHoursStringToScheduleEntries = (
  hoursString: string,
): ScheduleEntryPayload[] => {
  if (!hoursString.trim()) return [];

  return hoursString
    .split(";")
    .map((segment) => segment.trim())
    .filter(Boolean)
    .map((segment) => {
      const [rawDay, slots] = segment.split("=");
      const day = rawDay.trim().toUpperCase() as Weekday;
      const [morningSlot, eveningSlot] = slots.split("/");
      const [morningStart, morningEnd] = morningSlot.split("-");
      const [eveningStart, eveningEnd] = eveningSlot
        ? eveningSlot.split("-")
        : [null, null];
      return { day, morningStart, morningEnd, eveningStart, eveningEnd };
    });
};

/** Map a raw restaurant API response to the business type (legacy `numberTables` → `numberOfSeats`). */
export const mapRestaurantResponseToRestaurantType = (
  restaurant: RestaurantResponse,
): RestaurantType => {
  const { numberTables, ...rest } = restaurant;
  return { ...rest, numberOfSeats: numberTables };
};

export const mapRestrauntReadResponseToRestaurantDetails = (
  restaurantDetails: RestaurantReadResponse,
): RestaurantDetails => {
  const mappedDays = restaurantDetails.schedule.map((hour) => ({
    day: hour.day,
    morning: mapHourObjectResponseToHourObject(hour.morning),
    evening: hour.evening
      ? mapHourObjectResponseToHourObject(hour.evening)
      : null,
  }));

  const fullSchedule = WEEK_DAYS.map(
    (day) => mappedDays.find((h) => h.day === day) ?? closedDay(day),
  );

  return {
    ...restaurantDetails,
    schedule: fullSchedule,
    numberOfTables: restaurantDetails.numberOfTables ?? null,
    maximumPartySize: restaurantDetails.maximumPartySize ?? null,
  };
};

/**
 * `GET /restaurants/{id}` — the base read model plus the profile the
 * restaurant-information page edits. The profile fields pass through as-is; the
 * base mapper is what normalises the schedule.
 */
export const mapReadResponseToInfoDetails = (
  response: RestaurantInfoReadResponse,
): RestaurantInfoDetails => ({
  ...response,
  ...mapRestrauntReadResponseToRestaurantDetails(response),
});

/**
 * `GET /restaurants/{id}/table-schedule` and `.../opening-hours` — the base read
 * model plus the slot length.
 */
export const mapReadResponseToScheduleDetails = (
  response: RestaurantScheduleReadResponse,
): RestaurantScheduleDetails => ({
  ...response,
  ...mapRestrauntReadResponseToRestaurantDetails(response),
});
